import { c as _c } from "react/compiler-runtime";
import { cx } from '@7f-ui';
import { Chip } from '@7f-ui/components/chip/chip';
import { Price } from '@7f-ui/components/price';
import { uiTestId } from '@7f-ui/test-ids';
import type { AlternativeDateFragment } from '@data-access/lib/gql/accommodations/documents';
import { tz } from '@date-fns/tz';
import { formatDateWithShortYear } from '@utils/utils-format';
import { isSameDay } from 'date-fns';
import { useState } from 'react';
import { DEFAULT_TIME_ZONE } from '@/lib/time-zone';
type AppAlternativeDateChipProps = {
  alternativeDate: AlternativeDateFragment;
  isChipActive: boolean;
  isLowestPrice: boolean;
  onSelect: (dates: [Date, Date]) => void;
};
type AppAlternativeDateChipsProps = {
  alternativeDates: AlternativeDateFragment[] | undefined;
  dates: [Date | null, Date | null];
  onSelect: (dates: [Date, Date]) => void;
};
export function AppAlternativeDateChips(t0) {
  const $ = _c(14);
  const {
    alternativeDates,
    dates,
    onSelect
  } = t0;
  const [shownDates, setShownDates] = useState(alternativeDates);
  if (alternativeDates?.length && alternativeDates !== shownDates) {
    setShownDates(alternativeDates);
  }
  if (!shownDates?.length) {
    return null;
  }
  let t1;
  if ($[0] !== shownDates) {
    t1 = Math.min(...shownDates.map(_temp));
    $[0] = shownDates;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const minPrice = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <h4 className="font-body-copy font-semibold">Verfügbare Zeiträume dieser Unterkunft</h4>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== dates || $[4] !== minPrice || $[5] !== onSelect || $[6] !== shownDates) {
    let t4;
    if ($[8] !== dates || $[9] !== minPrice || $[10] !== onSelect) {
      t4 = alternativeDate_0 => {
        const isChipActive = isActive(dates, alternativeDate_0);
        return <AppAlternativeDateChip key={`${alternativeDate_0.checkin}-${alternativeDate_0.checkout}`} alternativeDate={alternativeDate_0} isChipActive={isChipActive} isLowestPrice={alternativeDate_0.price === minPrice} onSelect={onSelect} />;
      };
      $[8] = dates;
      $[9] = minPrice;
      $[10] = onSelect;
      $[11] = t4;
    } else {
      t4 = $[11];
    }
    t3 = shownDates.map(t4);
    $[3] = dates;
    $[4] = minPrice;
    $[5] = onSelect;
    $[6] = shownDates;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[12] !== t3) {
    t4 = <>{t2}<div className="flex flex-wrap gap-2 pb-4">{t3}</div></>;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
}
function _temp(alternativeDate) {
  return alternativeDate.price;
}
function isActive([from, to]: [Date | null, Date | null], alternativeDate: AlternativeDateFragment) {
  if (!from || !to) return false;
  return isSameDay(from, alternativeDate.checkin, {
    in: tz(DEFAULT_TIME_ZONE)
  }) && isSameDay(to, alternativeDate.checkout, {
    in: tz(DEFAULT_TIME_ZONE)
  });
}
function AppAlternativeDateChip(t0) {
  const $ = _c(25);
  const {
    alternativeDate,
    isChipActive,
    isLowestPrice,
    onSelect
  } = t0;
  let t1;
  if ($[0] !== alternativeDate.checkin || $[1] !== alternativeDate.checkout || $[2] !== onSelect) {
    t1 = () => {
      onSelect([new Date(alternativeDate.checkin), new Date(alternativeDate.checkout)]);
    };
    $[0] = alternativeDate.checkin;
    $[1] = alternativeDate.checkout;
    $[2] = onSelect;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const handleClick = t1;
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = uiTestId("DateModalContent.AlternativeDateChip");
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = `${formatDateWithShortYear(alternativeDate.checkin)} - ${formatDateWithShortYear(alternativeDate.checkout)}`;
  const t4 = alternativeDate.lengthOfStay === 1 ? "1 Nacht" : `${alternativeDate.lengthOfStay} Nächte`;
  let t5;
  if ($[5] !== t4) {
    t5 = <span>{t4}</span>;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = !isChipActive && isLowestPrice;
  let t7;
  if ($[7] !== t6) {
    t7 = cx({
      "text-success": t6
    });
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== alternativeDate.currency || $[10] !== alternativeDate.price) {
    t8 = <Price amount={alternativeDate.price} currency={alternativeDate.currency} />;
    $[9] = alternativeDate.currency;
    $[10] = alternativeDate.price;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t7 || $[13] !== t8) {
    t9 = <span className={t7}>{"ab "}{t8}</span>;
    $[12] = t7;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t5 || $[16] !== t9) {
    t10 = <p className="font-base-copy">{t5}{" "}{t9}</p>;
    $[15] = t5;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t3) {
    t11 = <div className="flex flex-col text-start">{t3}{t10}</div>;
    $[18] = t10;
    $[19] = t3;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== handleClick || $[22] !== isChipActive || $[23] !== t11) {
    t12 = <Chip variant="outlined" onClick={handleClick} active={isChipActive} data-testid={t2}>{t11}</Chip>;
    $[21] = handleClick;
    $[22] = isChipActive;
    $[23] = t11;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  return t12;
}